import { useLingui } from '@lingui/react/macro';
import { onlineManager } from '@tanstack/react-query';
import {
  createFileRoute,
  isNotFound,
  isRedirect,
  notFound,
  useLocation,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect, useState } from 'react';
import * as z from 'zod';

import { Chats } from '#modules/chat/components/Chats';
import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { isChatId, isChatNotFoundError } from '#modules/chat/utils/chatLink';
import { getChatById } from '#features/chats/model/chats';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { useNativeBackTo } from '#features/layout/hooks/useNativeBackTo';
import { useDismissChatNotification } from '#features/notifications/hooks/useDismissChatNotification';
import { Link } from '#features/routing';
import {
  restoreChatSnapshotSafely,
  seedCachedChatMetadata,
} from '#features/routing/utils/nativeCachedRoutes';
import { LoadFailed } from '#features/ui/components/LoadFailed';
import { optionalSearchString } from '#utils/searchParams';

export const Route = createFileRoute(
  '/{-$locale}/_layout/_dashboard/_authed/dashboard/chats/$chatId'
)({
  validateSearch: zodValidator(
    z.object({
      prefillMessage: optionalSearchString(),
      // Ставит кнопка «Call back» на пуше о пропущенном звонке (см. firebase-messaging-sw.js):
      // страница чата открывается и сразу набирает собеседника. Приезжает из адреса ЧИСЛОМ –
      // отсюда приведение к строке, иначе разбор роняет весь экран (см. optionalSearchString).
      callback: optionalSearchString(),
    })
  ),
  loader: async ({ params: { chatId }, context }) => {
    /*
      Битая ссылка и несуществующая переписка отвечают одним экраном «чат не найден» с дорогой в
      список, а не редиректом. Редирект из лоадера этого маршрута (он без SSR) оставлял белую
      страницу, а несуществующий номер с ответом шлюза без 404 вообще открывал пустой тред с
      полем ввода, будто переписка есть, только пока без сообщений.
    */
    if (!isChatId(chatId)) {
      throw notFound();
    }

    try {
      const chatOptions = {
        queryKey: ['chatById', chatId],
        queryFn: () => getChatById({ id: chatId }),
      };
      /*
        Приложение: переписка с телефона - в кеш до запроса (features/snapshot), и запрос берёт
        то, что уже лежит, а свежее дочитывает в фоне. Без сети `fetchQuery` здесь висел или
        через ~7 с падал, и открыть сохранённую переписку было нельзя. В вебе - как было.
      */
      if (import.meta.env.VITE_IS_NATIVE === 'true' && typeof window !== 'undefined') {
        await restoreChatSnapshotSafely(context.queryClient, chatId);
        seedCachedChatMetadata(context.queryClient, chatId);

        /*
          🔴 Снимка нет и сети нет - ждать нечего и некого. Чтения идут со стоковым
          `networkMode`, то есть без сети встают на ПАУЗУ: промис `ensureQueryData` не
          разрешается никогда, а страницы ожидания у маршрута нет - человек оставался в пустом
          экране навсегда, без единой надписи. Единственным выходом была перезагрузка.

          Признак снимка не годится сам по себе: `restoreChatSnapshot` отвечает `'skipped'`,
          если файл уже читали за этот запуск, - поэтому смотрим, лежит ли переписка в кеше.
        */
        const cached = context.queryClient.getQueryData(chatOptions.queryKey);
        if (!cached && !onlineManager.isOnline()) {
          // Обновление ставим в фон: пауза снимется сама, когда связь вернётся.
          void context.queryClient.prefetchQuery(chatOptions);
          return { offlineMissing: true } as const;
        }
      }
      const chat =
        import.meta.env.VITE_IS_NATIVE === 'true' && typeof window !== 'undefined'
          ? await context.queryClient.ensureQueryData({ ...chatOptions, revalidateIfStale: true })
          : await context.queryClient.fetchQuery(chatOptions);

      if (!chat?.id) {
        throw notFound();
      }

      return null;
    } catch (error) {
      // `notFound()` из блока выше попадает сюда же - пропускаем к `notFoundComponent`.
      if (isRedirect(error) || isNotFound(error)) throw error;
      console.error(error);

      if (isChatNotFoundError(error)) {
        throw notFound();
      }

      // Don't crash the route — component will retry via useSuspenseQuery
      return null;
    }
  },
  component: RouteComponent,
  notFoundComponent: ChatNotFound,
  ssr: false,
});

function ChatNotFound() {
  const { t } = useLingui();

  return (
    <ContentNotFound
      title={t`notFound.chat.title`}
      description={t`notFound.chat.description`}
      action={<Link to="/{-$locale}/dashboard/chats">{t`Back to chats`}</Link>}
    />
  );
}

function RouteComponent() {
  const { chatId } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const { prefillMessage, callback } = Route.useSearch();
  const navigate = Route.useNavigate();
  const fromChatList = useLocation().state.fromChatList === true;

  /**
   * Постоянный родитель переписки - список чатов, а не «шаг назад по истории».
   *
   * Открыть переписку можно из списка, с главной, из профиля собеседника и по уведомлению, и в
   * трёх случаях из четырёх системная «Назад» уводила куда угодно, только не в список: человек
   * жал её, чтобы выйти из разговора, и попадал на главную, где внизу снова подсвечивался «Home».
   *
   * Пришли из списка - отматываем историю: список уже лежит шагом раньше, и новый переход дал бы
   * второй такой же шаг, а следующее нажатие вернуло бы человека в тот же список - впустую.
   * Пришли откуда-то ещё - заменяем шаг переписки списком, чтобы «Назад» из него уводила туда,
   * где человек был до разговора.
   */
  useNativeBackTo(
    useCallback(() => {
      if (fromChatList) {
        window.history.back();
        return;
      }
      void navigate({ to: '/{-$locale}/dashboard/chats', replace: true });
    }, [fromChatList, navigate])
  );

  /**
   * «Перезвонить» срабатывает ровно один раз. Решение живёт в состоянии, а из адреса флаг
   * снимается сразу: оставленный в строке, он звонил бы заново на каждое обновление страницы, а
   * снятый до того, как шапка чата успела смонтироваться, потерял бы звонок вовсе.
   */
  const [autoCall] = useState(() => callback === '1');
  useEffect(() => {
    if (callback) {
      void navigate({ search: (prev) => ({ ...prev, callback: undefined }), replace: true });
    }
  }, [callback, navigate]);

  // Mark this conversation as the one on screen so the global WS handler
  // suppresses the toast + sound for messages that belong to it. Счётчик ссылок, а не единственный
  // id: ту же переписку рядом держит открытой окно комнаты сессии.
  const registerChat = useActiveChatStore((s) => s.registerChat);
  const unregisterChat = useActiveChatStore((s) => s.unregisterChat);
  useEffect(() => {
    registerChat(chatId);
    return () => unregisterChat(chatId);
  }, [chatId, registerChat, unregisterChat]);

  // Разговор на экране — уведомлению о нём в шторке делать нечего.
  useDismissChatNotification(chatId);

  /*
    Переписки нет ни на телефоне, ни в сети: сказать об этом - единственное, что здесь можно
    сделать честно. Раньше на этом месте был пустой экран навсегда, потому что чтение стояло на
    паузе и лоадер не завершался.

    Подгрузка уже поставлена в фон лоадером: как только связь вернётся, пауза снимется, переписка
    приедет, и эта страница сменится сама. Поэтому кнопки «повторить» тут нет - нечего повторять.
  */
  if (loaderData?.offlineMissing) {
    return <LoadFailed offline onRetry={undefined} />;
  }

  return <Chats chatId={chatId} prefillMessage={prefillMessage} autoCall={autoCall} />;
}
